import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Users, Power } from "lucide-react";
import { api } from "@/lib/api";
import { formatDate, formatDateOnly } from "@/lib/cn";
import { Badge } from "@/components/Badge";
import { QueryError } from "@/components/QueryError";
import { AdminRecordActions } from "@/components/AdminRecordActions";
import { StaffFormModal } from "./StaffFormModal";
import { ForceDeleteDialog, isForceable, type Dependants } from "@/components/ForceDeleteDialog";

interface Staff {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  startDate: string;
  isActive: boolean;
  email?: string | null;
  phone?: string | null;
  description?: string | null;
  assignedBuildings?: string[];
  _count: { tasks: number; timeEntries: number };
}

export function StaffPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Staff | null>(null);
  const [building, setBuilding] = useState("");

  const { data: staff = [], isLoading, isError } = useQuery({
    queryKey: ["staff"],
    queryFn: async () => (await api.get<Staff[]>("/staff")).data,
  });
  // The gardens are split by building: filter to whoever covers one.
  const buildingOptions = [...new Set(staff.flatMap((s) => s.assignedBuildings ?? []))].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
  const visible = building ? staff.filter((s) => s.assignedBuildings?.includes(building)) : staff;

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.put(`/staff/${id}`, { isActive })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["staff"] }),
  });

  // Staff with tasks or time entries are refused (409 HAS_DEPENDANTS); the admin may
  // override through a second, explicit confirmation that sends ?force=true.
  const [forceTarget, setForceTarget] = useState<{ id: string; dependants: Dependants } | null>(null);
  const deleteMutation = useMutation({
    mutationFn: async ({ id, force }: { id: string; force?: boolean }) =>
      (await api.delete(`/staff/${id}${force ? "?force=true" : ""}`)).data,
    onSuccess: () => {
      setForceTarget(null);
      qc.invalidateQueries({ queryKey: ["staff"] });
    },
    onError: (e: any, vars) => {
      const dependants = isForceable(e);
      if (dependants && !vars.force) {
        setForceTarget({ id: vars.id, dependants });
        return;
      }
      alert(e?.response?.data?.message ?? "Error");
    },
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users size={24} /> {t("hr.staff")}
        </h1>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} /> {t("hr.newStaff")}
        </button>
      </div>

      {buildingOptions.length > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor="staff-building" className="text-slate-600">{t("hr.building")}</label>
          <select id="staff-building" className="input max-w-[10rem]" value={building} onChange={(e) => setBuilding(e.target.value)}>
            <option value="">{t("hr.allBuildings")}</option>
            {buildingOptions.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </div>
      )}

      <div className="card p-0 overflow-x-auto">
        {isError ? (
          <div className="p-6"><QueryError /></div>
        ) : isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : visible.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">Nombre</th>
                <th className="text-left px-4 py-2">{t("hr.position")}</th>
                <th className="text-left px-4 py-2">{t("hr.startDate")}</th>
                <th className="text-left px-4 py-2">Contacto</th>
                <th className="text-left px-4 py-2">Tareas / Tiempo</th>
                <th className="text-left px-4 py-2">{t("finance.status")}</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {visible.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2">
                    <Link to={`/admin/staff/${s.id}`} className="font-medium text-primary hover:underline">
                      {s.firstName} {s.lastName}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <div>{s.position}</div>
                    {s.description && (
                      <div className="text-xs text-slate-500 max-w-[18rem] truncate" title={s.description}>{s.description}</div>
                    )}
                    {!!s.assignedBuildings?.length && (
                      <div className="flex flex-wrap gap-1 mt-1" aria-label={t("hr.assignedBuildings")}>
                        {s.assignedBuildings.map((b) => (
                          <span key={b} className="text-[11px] leading-none px-1.5 py-1 rounded bg-primary-50 text-primary-800 border border-primary-100">
                            {b}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">{formatDateOnly(s.startDate, i18n.language)}</td>
                  <td className="px-4 py-2">
                    {s.email && <div>{s.email}</div>}
                    {s.phone && <div className="text-xs text-slate-500">{s.phone}</div>}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{s._count.tasks} tareas · {s._count.timeEntries} entradas</td>
                  <td className="px-4 py-2">
                    <Badge variant={s.isActive ? "success" : "default"}>{s.isActive ? t("hr.active") : t("hr.inactive")}</Badge>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex items-center gap-3">
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => toggleMutation.mutate({ id: s.id, isActive: !s.isActive })}
                      >
                        <Power size={12} /> {s.isActive ? t("hr.deactivate") : t("hr.activate")}
                      </button>
                      <AdminRecordActions
                        compact
                        entityLabel={`${s.firstName} ${s.lastName}`}
                        onEdit={() => setEditing(s)}
                        onDelete={() => deleteMutation.mutate({ id: s.id })}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <ForceDeleteDialog
        open={!!forceTarget}
        dependants={forceTarget?.dependants ?? null}
        pending={deleteMutation.isPending}
        onCancel={() => setForceTarget(null)}
        onConfirm={() => forceTarget && deleteMutation.mutate({ id: forceTarget.id, force: true })}
      />
      <StaffFormModal
        open={open || !!editing}
        target={editing}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        onDone={() => qc.invalidateQueries({ queryKey: ["staff"] })}
      />
    </div>
  );
}
